/**
 * Title screen: brand, tagline, the seven doors, Clausewitz on a scrap of paper, a language switch
 * — and, until the player has answered it, a telegram inviting new officers to the
 * Kriegsakademie.
 */
import { navStore, type Screen } from '../../app/nav';
import { settingsStore } from '../../app/settings';
import { bus } from '../../game/bus';
import { LOCALES, type Translator, useT } from '../../i18n';
import { useLobby } from '../../net/lobby';
import { startAcademy } from '../../tutorial/launch';
import { tutorialStore, useTutorial } from '../../tutorial/store';
import { Button, cx } from '../common/controls';
import { Paper, Stamp } from '../common/Panel';
import s from './menu.module.css';

const go = (screen: Screen) => () => navStore.getState().go(screen);

const items = (t: Translator): { key: string; num: string; label: string; desc: string; open(): void }[] => [
  { key: 'sp', num: 'I', label: t('menu.sp.label'), desc: t('menu.sp.desc'), open: go('singleplayer') },
  {
    key: 'scenarios',
    num: 'II',
    label: t('menu.scenarios.label'),
    desc: t('menu.scenarios.desc'),
    open: go('scenarios'),
  },
  {
    key: 'academy',
    num: 'III',
    label: t('menu.academy.label'),
    desc: t('menu.academy.desc'),
    open: () => startAcademy(),
  },
  { key: 'mp', num: 'IV', label: t('menu.mp.label'), desc: t('menu.mp.desc'), open: go('multiplayer') },
  {
    key: 'archive',
    num: 'V',
    label: t('menu.archive.label'),
    desc: t('menu.archive.desc'),
    open: go('archive'),
  },
  {
    key: 'manual',
    num: 'VI',
    label: t('menu.manual.label'),
    desc: t('menu.manual.desc'),
    open: go('manual'),
  },
  {
    key: 'settings',
    num: 'VII',
    label: t('menu.settings.label'),
    desc: t('menu.settings.desc'),
    open: go('settings'),
  },
];

/** First launch: "New to the staff?" — until enrolled, graduated or declined. */
function AcademyOffer() {
  const t = useT();
  const show = useTutorial((x) => !x.completed && !x.offerDismissed);
  if (!show) return null;
  return (
    <Paper className={s.offer}>
      <div className={s.offerHead}>
        <span>{t('menu.offer.head')}</span>
        <span>{t('menu.offer.urgent')}</span>
      </div>
      <div className={s.offerTitle}>{t('menu.offer.title')}</div>
      <p className={s.offerText}>{t('menu.offer.text')}</p>
      <div className={s.offerBtns}>
        <Button variant="primary" size="sm" icon="book" onClick={() => startAcademy()}>
          {t('menu.offer.enrol')}
        </Button>
        <Button variant="ink" size="sm" onClick={() => tutorialStore.getState().dismissOffer()}>
          {t('menu.offer.notNow')}
        </Button>
      </div>
      <Stamp className={s.offerStamp} tone="blue" rotate={9} animate delay={1500}>
        {t('menu.offer.stamp')}
      </Stamp>
    </Paper>
  );
}

/** EN · DE — a pair of brass tabs under the menu; switches the whole interface at once. */
function LanguageSwitch() {
  const t = useT();
  return (
    <nav className={s.lang} aria-label={t('menu.lang.aria')}>
      {LOCALES.map((l) => (
        <button
          key={l}
          type="button"
          aria-pressed={t.locale === l}
          lang={l}
          className={cx(s.langBtn, t.locale === l && s.langOn)}
          onClick={() => {
            bus.emit('sound', { name: 'click' });
            settingsStore.getState().set({ language: l });
          }}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </nav>
  );
}

export function MainMenu() {
  const t = useT();
  const status = useLobby((l) => l.status);
  return (
    <div className={s.main}>
      <aside className={s.sidebar}>
        <div className={s.kicker}>{t('menu.kicker')}</div>
        <h1 className={s.title}>FRICTION</h1>
        <p className={s.tagline}>
          <span>{t('menu.tagline1')}</span>
          <span>{t('menu.tagline2')}</span>
        </p>

        <nav className={s.menu} aria-label={t('menu.aria')}>
          {items(t).map((it) => (
            <button
              key={it.key}
              type="button"
              className={s.item}
              onMouseEnter={() => bus.emit('sound', { name: 'type', volume: 0.7 })}
              onClick={() => {
                bus.emit('sound', { name: 'click' });
                it.open();
              }}
            >
              <span className={s.num}>{it.num}.</span>
              <span className={s.label}>{it.label}</span>
              <span className={s.desc}>{it.desc}</span>
            </button>
          ))}
        </nav>

        <footer className={s.footer}>
          <LanguageSwitch />
          <span className={s.status}>
            <span
              className={cx(s.led, status === 'online' && s.ledOn, status === 'connecting' && s.ledAmber)}
            />
            {t('menu.net.label')} ·{' '}
            {status === 'online'
              ? t('menu.net.online')
              : status === 'connecting'
                ? t('menu.net.connecting')
                : t('menu.net.offline')}
          </span>
          <span>{t('menu.build')}</span>
        </footer>
      </aside>

      <Paper className={s.quote}>
        <Stamp className={s.quoteStamp} rotate={4}>
          {t('menu.quote.stamp')}
        </Stamp>
        <p className={s.quoteText}>{t('menu.quote.text')}</p>
        <div className={s.quoteBy}>{t('menu.quote.by')}</div>
      </Paper>
      <AcademyOffer />
    </div>
  );
}
